/* eslint-disable no-console */
import styled from '@emotion/styled'
import { White } from '@mehilainen/mds-customer/colors'
import { Alert, Snackbar } from '@mui/material'
import { useAtom, useAtomValue, useSetAtom, ExtractAtomValue } from 'jotai'
import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom'

import { NodeType, SourceService } from './__generated__/api'
import ExternalBanner from './common/components/ExternalBanner/ExternalBanner'
import ExternalRedirect from './common/components/ExternalRedirect/ExternalRedirect'
import Footer from './common/components/Footer/Footer'
import ImpersonationBanner from './common/components/ImpersonationBanner/ImpersonationBanner'
import NavBar from './common/components/NavBar/NavBar'
import NodeRedirectModal from './common/components/NodeRedirectModal/NodeRedirectModal'
import PartnerRedirect from './common/components/PartnerRedirect/PartnerRedirect'
import PractitionerInfoModal from './common/components/PractitionerInfoModal/PractitionerInfoModal'
import ScheduledMaintenance from './common/components/ScheduledMaintenance/ScheduledMaintenance'
import SetContractImpersonation from './common/components/SetContractImpersonation/SetContractImpersonation'
import TabTitle from './common/components/TabTitle/TabTitle'
import {
  GLOBAL_PARAMETERS,
  SEARCH_PARAMETERS,
  APPOINTMENT_PARAMETERS,
  COOKIE_PARAMETERS,
} from './common/constants/urlParameters'
import { useBackendTranslations } from './common/hooks/useBackendTranslations'
import useChangeLanguage from './common/hooks/useChangeLanguage'
import { useCookie } from './common/hooks/useCookie'
import useLoginState from './common/hooks/useLoginState'
import useMobileObserver from './common/hooks/useMobileObserver'
import Sentry from './common/services/sentry'
import { maxWidth } from './common/utils/breakpoint'
import { filterExternalUrlParameters } from './common/utils/url'
import AppointmentOptionsSelectContainer from './domain/appointmentOptionsSelect/container/AppointmentOptionsSelectContainer'
import AppointmentOptionSelectView from './domain/appointmentOptionsSelect/container/AppointmentOptionsSelectView'
import AppointmentCancellationLegacyView from './domain/cancellation/container/AppointmentCancellationLegacyView'
import AppointmentCancellationView from './domain/cancellation/container/AppointmentCancellationView'
import BookingConfirmationView from './domain/confirmation/container/BookingConfirmationView'
import Modals from './domain/modals/Modals'
import PreselectionView from './domain/preselection/container/PreselectionView'
import InsuranceRedirect from './domain/reserve/components/InsuranceRedirect'
import ReserveView from './domain/reserve/container/ReserveView'
import SearchView from './domain/search/container/SearchView'
import {
  modalTriggererAtom as modalTriggerer,
  selectedAppointmentIdAtom as aid,
  selectedPractitionerIdAtom as pid,
} from './state/appointmentOptionsSelect/atoms'
import {
  generalErrorMessageAtom,
  isFromAppAtom,
  isFromOMAtom,
  selectedAppointmentIdAtom,
  selectedAppointmentLengthAtom,
  selectedAppointmentNodeConnectionIdAtom,
  selectedAppointmentNodeIdAtom,
  selectedAppointmentServiceIdAtom,
  sourceServiceAtom,
} from './state/common/atoms'
import {
  practitionerModalPractitionerId,
  selectedDateAtom as selectedPractitionerDateAtom,
  selectedPractitionerIdAtom,
} from './state/practitioner/atoms'
import { selectedAppointmentDetailsAtom } from './state/reserve/atoms'
import {
  defaultDentalSearchNodeId,
  searchServiceModalAutoOpenAtom,
  callbackModalOpenAtom,
  searchServiceModalOpenAtom,
} from './state/search/atoms'
import { useSearchEvents } from './xstate/events'
import { useAppointmentSearchMode, useSelectedDate } from './xstate/selectors'
import { SearchTargetValue } from './xstate/types'

const Main = styled.main`
  background: ${White};

  @media (min-width: ${maxWidth.body + 1}px) {
    width: ${maxWidth.body}px;
    margin: auto;
  }
`

export const reserveRoutePath = 'reserve'

interface Props {
  basePath: string
}

const Routes: React.FC<React.PropsWithChildren<Props>> = ({ basePath }) => {
  const { t } = useTranslation()
  const { changeLanguage, changeNamespace } = useChangeLanguage()
  useBackendTranslations()
  const history = useHistory()

  const setSelectedPractitionerId = useSetAtom(selectedPractitionerIdAtom)
  const setPractitionerDate = useSetAtom(selectedPractitionerDateAtom)
  const setPractitionerAppointmentDuration = useSetAtom(selectedAppointmentLengthAtom)
  const setPractitionerModalPractitionerId = useSetAtom(practitionerModalPractitionerId)
  const setSelectedAppointmentId = useSetAtom(selectedAppointmentIdAtom)
  const setSelectedAppointmentNodeId = useSetAtom(selectedAppointmentNodeIdAtom)
  const setSelectedAppointmentServiceId = useSetAtom(selectedAppointmentServiceIdAtom)
  const setSelectedNodeConnectionId = useSetAtom(selectedAppointmentNodeConnectionIdAtom)
  const selectedDate = useSelectedDate()
  const setTriggerer = useSetAtom(modalTriggerer)
  const setAid = useSetAtom(aid)
  const setPid = useSetAtom(pid)
  const setSelectedAppointmentDetails = useSetAtom(selectedAppointmentDetailsAtom)
  const { setSearchTarget } = useSearchEvents()
  const appointmentSearchMode = useAppointmentSearchMode()
  const setServiceModalAutoOpen = useSetAtom(searchServiceModalAutoOpenAtom)

  const isFromApp = useAtomValue(isFromAppAtom)

  const openAppointmentModal = useCallback(
    (
      triggerer: ExtractAtomValue<typeof modalTriggerer>,
      appointmentId: ExtractAtomValue<typeof aid>,
      practitionerId: ExtractAtomValue<typeof pid>
    ) => {
      setTriggerer(triggerer)
      setAid(appointmentId)
      setPid(practitionerId)
    },
    [setTriggerer, setAid, setPid]
  )

  const [generalErrorMessage, setGeneralErrorMessage] = useAtom(generalErrorMessageAtom)

  const sessionExpiredCallback = useCallback(
    () => setGeneralErrorMessage('common.sessionExpired'),
    [setGeneralErrorMessage]
  )

  const { loginStatus, logout } = useLoginState(sessionExpiredCallback)

  useEffect(
    () =>
      history.listen((_, action) => {
        if (action !== 'REPLACE') {
          window.scrollTo(0, 0)
        }
      }),
    [history]
  )

  const location = useLocation()
  const prevLocation: { current: typeof location } = useRef(location)

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      data: {
        from: {
          pathname: prevLocation.current.pathname,
          search: Object.fromEntries(new URLSearchParams(prevLocation.current.search)),
        },
        to: {
          pathname: location.pathname,
          search: Object.fromEntries(new URLSearchParams(location.search)),
        },
      },
    })

    prevLocation.current = location
  }, [location])

  const setIsFromApp = useSetAtom(isFromAppAtom)
  const setIsFromOM = useSetAtom(isFromOMAtom)
  const setSourceService = useSetAtom(sourceServiceAtom)
  const [isFromAppCookie, updateIsFromAppCookie] = useCookie(COOKIE_PARAMETERS.MOBILE, '0')
  const setCallbackModalOpen = useSetAtom(callbackModalOpenAtom)
  const setServiceModalOpen = useSetAtom(searchServiceModalOpenAtom)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.has(GLOBAL_PARAMETERS.IS_FROM_APP) || isFromAppCookie === '1') {
      setIsFromApp(true)
      updateIsFromAppCookie('1')
    }
    if (searchParams.has(GLOBAL_PARAMETERS.IS_FROM_OM)) {
      setIsFromOM(true)
    }

    if (searchParams.has(GLOBAL_PARAMETERS.SOURCE_SERVICE)) {
      const sourceService = searchParams.get(GLOBAL_PARAMETERS.SOURCE_SERVICE) ?? undefined
      setSourceService(sourceService as SourceService)
    }

    if (searchParams.has(GLOBAL_PARAMETERS.TRANSLATION_NAMESPACE)) {
      changeNamespace(searchParams.get(GLOBAL_PARAMETERS.TRANSLATION_NAMESPACE) ?? '')
    }
    if (searchParams.has(SEARCH_PARAMETERS.NO_SERVICE_SELECT)) {
      setServiceModalAutoOpen(false)
    }
    if (searchParams.has(APPOINTMENT_PARAMETERS.CALLBACK_OPEN)) {
      setCallbackModalOpen(true)
    }
    if (searchParams.has(SEARCH_PARAMETERS.SERVICE_SELECT)) {
      setServiceModalOpen(true)
    }
  }, [
    location,
    setIsFromApp,
    setIsFromOM,
    isFromAppCookie,
    updateIsFromAppCookie,
    changeNamespace,
    setServiceModalAutoOpen,
    setCallbackModalOpen,
    setServiceModalOpen,
    setSourceService,
  ])

  // Observe topmost element and update mobile header accordingly
  useMobileObserver(isFromApp)

  return (
    <>
      <ScheduledMaintenance />

      <ExternalBanner loginStatus={loginStatus} />
      <ImpersonationBanner loginStatus={loginStatus} />

      <Snackbar
        autoHideDuration={10000}
        open={Boolean(generalErrorMessage)}
        onClose={() => setGeneralErrorMessage(undefined)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error">{t(generalErrorMessage ?? '')}</Alert>
      </Snackbar>

      <AppointmentOptionsSelectContainer
        onSelect={(appointmentId, choices) => {
          setSelectedAppointmentId(appointmentId)
          const searchParams = filterExternalUrlParameters(location.search).toString() // Preserve external parameters
          if (choices.duration === 'default' || typeof choices.duration === 'number') {
            setPractitionerAppointmentDuration(choices.duration)
            history.push(
              `${basePath}/appointment/${appointmentId}?${APPOINTMENT_PARAMETERS.LENGTH}=${choices.duration}&${APPOINTMENT_PARAMETERS.TYPE}=${choices.preselectedType}&${APPOINTMENT_PARAMETERS.SERVICE}=${choices.serviceId}&${searchParams}`
            )
            return
          }
          history.push(
            `${basePath}/appointment/${appointmentId}?${APPOINTMENT_PARAMETERS.TYPE}=${choices.preselectedType}&${APPOINTMENT_PARAMETERS.SERVICE}=${choices.serviceId}&${searchParams}`
          )
        }}
        onSelectUnavailableDuration={(practitionerId, duration) => {
          setSelectedPractitionerId(practitionerId)
          setPractitionerDate(selectedDate)
          setPractitionerAppointmentDuration(duration)
          setSearchTarget({ id: practitionerId, value: SearchTargetValue.Practitioner })
        }}
      />

      <PractitionerInfoModal
        onDisplayAppointmentsClick={(practitionerId) => {
          setSearchTarget({ id: practitionerId, value: SearchTargetValue.Practitioner })
          history.push(basePath)
          setPractitionerModalPractitionerId(undefined)
        }}
      />

      <Main>
        <Modals />
        <Route exact path={basePath === '' ? '/' : basePath}>
          <NodeRedirectModal />
          <TabTitle value={t('component.tabTitle.search.title')} />
          <React.Suspense fallback={<></>}>
            {!isFromApp && (
              <NavBar
                onLanguageSelect={changeLanguage}
                loginStatus={loginStatus}
                logoutCallBack={logout}
                variant="nested"
              />
            )}
            <SearchView
              onAppointmentSelect={(selection) => {
                const { appointment } = selection
                setSelectedAppointmentDetails(appointment)
                const appointmentId = appointment.assisDentSearchResult
                  ? appointment.assisDentSearchResult.id
                  : appointment.appointmentId
                const searchParams = filterExternalUrlParameters(location.search) // Preserve external parameters
                if ('node' in selection) {
                  setSelectedAppointmentNodeId(selection.node.id)
                  setSelectedNodeConnectionId(appointment.connectionId)
                  setSelectedAppointmentServiceId(undefined)

                  if (
                    selection.node.type === NodeType.ServicePackage ||
                    appointment.assisDentSearchResult
                  ) {
                    setSelectedAppointmentId(appointmentId)

                    history.push(
                      `${basePath}/appointment/${appointmentId}?${APPOINTMENT_PARAMETERS.LENGTH}=${
                        appointment.duration
                      }&${APPOINTMENT_PARAMETERS.NODE}=${
                        selection.node.id
                      }&${searchParams.toString()}`
                    )
                    return
                  }

                  if (typeof appointmentId === 'number') {
                    openAppointmentModal(
                      'appointmentSelect',
                      appointmentId,
                      appointment.specialistId
                    )
                  }

                  return
                } else if ('practitionerId' in selection) {
                  setSelectedAppointmentNodeId(selection.nodeId)
                  setSelectedNodeConnectionId(undefined)
                  setSelectedAppointmentId(appointmentId)
                  setSelectedPractitionerId(selection.practitionerId)

                  if (selection.duration) {
                    searchParams.append(APPOINTMENT_PARAMETERS.LENGTH, String(selection.duration))
                  }

                  if (selection.serviceId) {
                    searchParams.append(APPOINTMENT_PARAMETERS.SERVICE, String(selection.serviceId))
                  }
                  searchParams.append(
                    APPOINTMENT_PARAMETERS.SPECIALTY,
                    String(selection.specialtyId)
                  )
                  searchParams.append(APPOINTMENT_PARAMETERS.MODE, appointmentSearchMode)

                  history.push(`${basePath}/appointment/${appointmentId}?${searchParams}`)
                }
              }}
              onPractitionerDetailsClick={setPractitionerModalPractitionerId}
            />
          </React.Suspense>
        </Route>

        <Route
          exact
          path={`${basePath}/insurance`}
          render={({ location: routeLocation }) => {
            return (
              <Redirect
                to={`${basePath}/${
                  routeLocation.search
                    ? `${routeLocation.search}&${GLOBAL_PARAMETERS.NO_LANDING}=insurance`
                    : `?${GLOBAL_PARAMETERS.NO_LANDING}=insurance`
                }`}
              />
            )
          }}
        />

        <Route
          exact
          path={`${basePath}/practitioner/:practitionerId?`}
          render={(props) => (
            <Redirect
              to={`${basePath}?${SEARCH_PARAMETERS.PRACTITIONER_ID}=${props.match.params.practitionerId}`}
            />
          )}
        />

        <Route exact path={`${basePath}/preselect`}>
          <PreselectionView basePath={basePath} />
        </Route>

        <Route exact path={`${basePath}/appointment/:appointmentId?`}>
          {!isFromApp && (
            <NavBar
              onLanguageSelect={changeLanguage}
              loginStatus={loginStatus}
              logoutCallBack={logout}
              variant="detached"
            />
          )}
          <TabTitle value={t('component.tabTitle.confirmSelect.title')} />
          <AppointmentOptionSelectView
            onSelect={(appointmentId) => {
              history.push(`${basePath}/reserve/${appointmentId}`)
            }}
            onBack={
              (/* appointmentId, practitionerId */) => {
                // FIXME: We only want to trigger the modal if coming from the search view.
                // openAppointmentModal('back', appointmentId, practitionerId)
                history.goBack()
              }
            }
            onNavigate={(searchTarget) => {
              setSearchTarget(searchTarget)
              history.push(basePath)
            }}
          />
        </Route>

        <Route exact path={`${basePath}/${reserveRoutePath}/:appointmentId?`}>
          {!isFromApp && (
            <NavBar
              onLanguageSelect={changeLanguage}
              loginStatus={loginStatus}
              logoutCallBack={logout}
              variant="detached"
            />
          )}
          <TabTitle value={t('component.tabTitle.confirmReservation.title')} />
          <ReserveView
            onBack={history.length <= 2 ? () => history.push(basePath) : () => history.goBack()}
            onReserved={(reserveResult) =>
              history.push(
                `${basePath}/confirmation-info/${reserveResult.reservationId}/${reserveResult.appointmentId}`
              )
            }
            onBackToStart={() => history.push(basePath)}
          />
        </Route>

        <Route exact path={`${basePath}/confirmation-info/:reservationId/:appointmentId`}>
          {!isFromApp && (
            <NavBar
              onLanguageSelect={changeLanguage}
              loginStatus={loginStatus}
              logoutCallBack={logout}
              variant="detached"
            />
          )}
          <TabTitle value={t('component.tabTitle.confirmThankYou.title')} />
          <BookingConfirmationView onBackToStart={() => history.push(basePath)} />
        </Route>

        <Route exact path={`${basePath}/cancel-appointment/:reservationId/:appointmentId`}>
          <NavBar
            onLanguageSelect={changeLanguage}
            loginStatus={loginStatus}
            logoutCallBack={logout}
            variant="detached"
          />
          <TabTitle value={t('component.tabTitle.cancelReservation.title')} />
          <AppointmentCancellationView />
        </Route>

        <Route exact path={`${basePath}/cancel`}>
          <NavBar
            onLanguageSelect={changeLanguage}
            loginStatus={loginStatus}
            logoutCallBack={logout}
            variant="detached"
          />
          <TabTitle value={t('component.tabTitle.cancelReservation.title')} />
          <AppointmentCancellationLegacyView />
        </Route>

        <Route
          exact
          path={`${basePath}/external`}
          render={() => (
            <ExternalRedirect
              basePath={basePath}
              redirectPath={`${process.env.REACT_APP_MEHIDOC_BASE_URL}/asiakaspalvelu/external`}
              loginStatus={loginStatus}
              onLogout={logout}
            />
          )}
        />

        <Route
          exact
          path={`${basePath}/partner`}
          render={() => <PartnerRedirect redirectPath={basePath} />}
        />
        <Route
          exact
          path={`${basePath}/set-contract`}
          render={() => <SetContractImpersonation />}
        />

        <InsuranceRedirect basePath={basePath} />

        <Route
          exact
          path={`${basePath}/hammashoito`}
          render={(props) => {
            setSearchTarget({
              id: defaultDentalSearchNodeId,
              value: SearchTargetValue.Node,
            })

            const search = new URLSearchParams(props.location.search)
            search.set(GLOBAL_PARAMETERS.NO_LANDING, 'dental')
            search.set(SEARCH_PARAMETERS.NODE_ID, defaultDentalSearchNodeId)

            return <Redirect to={`${basePath}?${search}`} />
          }}
        />

        <Route
          exact
          path="/help"
          render={() => {
            window.location.assign(t('redirect.help'))
            return null
          }}
        />

        <Route
          exact
          path="/callrequest"
          render={() => {
            return (
              <Redirect
                to={`${basePath}?${APPOINTMENT_PARAMETERS.CALLBACK_OPEN}&${GLOBAL_PARAMETERS.NO_LANDING}`}
              />
            )
          }}
        />

        <Route
          exact
          path="/callback"
          render={() => {
            return (
              <Redirect
                to={`${basePath}?${APPOINTMENT_PARAMETERS.CALLBACK_OPEN}&${GLOBAL_PARAMETERS.NO_LANDING}`}
              />
            )
          }}
        />

        {!isFromApp && <Footer />}
      </Main>
    </>
  )
}

export default Routes
